/**
 * Slot suggestion interleaving — mirrors iOS SlotSuggestionInterleaver +
 * SlotViatorProductFilter (AISuggestionModels).
 */

import type { ViatorProduct } from '../services/viatorProductsService';
import { isMealProduct } from '../services/viatorProductsService';
import type { AggregatedPlanItem, PlanTimeSlot } from './aggregatedPlanData';
import {
  isTourSlotPosition,
  type SightseeingStyle,
} from './sightseeingStyle';

export type SlotSuggestionEntry =
  | { kind: 'plan'; item: AggregatedPlanItem }
  | { kind: 'viator'; product: ViatorProduct; timeSlot: PlanTimeSlot };

const ACTIVITY_SLOTS: PlanTimeSlot[] = ['morning', 'afternoon', 'evening', 'night'];
const MEAL_SLOTS: PlanTimeSlot[] = ['breakfast', 'lunch', 'dinner'];

export function isMealSlot(slot: PlanTimeSlot): boolean {
  return MEAL_SLOTS.includes(slot);
}

export function isActivitySlot(slot: PlanTimeSlot): boolean {
  return ACTIVITY_SLOTS.includes(slot);
}

/** Filter Viator products for a slot — meals vs activities. */
export function filterViatorForSlot(
  products: ViatorProduct[],
  slot: PlanTimeSlot
): ViatorProduct[] {
  if (isActivitySlot(slot)) {
    return products.filter((p) => !isMealProduct(p));
  }
  if (isMealSlot(slot)) {
    return products.filter((p) => isMealProduct(p));
  }
  return [];
}

/**
 * Interleave catalog/plan items with tours by sightseeing style position rules.
 */
export function buildSlotSuggestionInterleave(options: {
  catalog: AggregatedPlanItem[];
  tours: ViatorProduct[];
  sightseeingStyle: SightseeingStyle;
  timeSlot: PlanTimeSlot;
  maxItems?: number;
}): SlotSuggestionEntry[] {
  const { catalog, tours, sightseeingStyle, timeSlot } = options;
  const limit = options.maxItems ?? catalog.length + tours.length;
  if (limit <= 0) return [];

  const result: SlotSuggestionEntry[] = [];
  let catalogIndex = 0;
  let tourIndex = 0;

  for (let position = 1; position <= limit; position += 1) {
    if (isTourSlotPosition(sightseeingStyle, position)) {
      if (tourIndex < tours.length) {
        result.push({ kind: 'viator', product: tours[tourIndex], timeSlot });
        tourIndex += 1;
      } else if (catalogIndex < catalog.length) {
        result.push({ kind: 'plan', item: catalog[catalogIndex] });
        catalogIndex += 1;
      } else {
        break;
      }
    } else if (catalogIndex < catalog.length) {
      result.push({ kind: 'plan', item: catalog[catalogIndex] });
      catalogIndex += 1;
    } else {
      break;
    }
  }

  return result;
}
